import type { IconSvgElement } from "@hugeicons/react";
import {
  LayoutDashboard,
  List,
  ListChecks,
  ShoppingCart,
  Package,
  DollarSign,
  Clock,
  Box,
  FileCheck,
  Archive,
  QrCode,
  RotateCcw,
  ClipboardList,
  Upload,
  FileArchive,
  Layers,
  Paperclip,
  TreePine,
  Door,
  Paintbrush,
  PanelRight,
  Cog,
  Users,
  UserCog,
  UserPlus,
  UserSearch,
  FileText,
  BarChart,
  Receipt,
  CreditCard,
  Percent,
  UserRound,

  MapPin,
  Settings,
  Megaphone,
  KeyRound,
  BookOpen,
  FolderOpen,
  AlertCircle,
  AiBrainIcon,
} from "@hugeicons/core-free-icons";
import type { UserRole } from "@/features/accounts/types/account.types";

import type { FeatureFlagName } from "@/features/feature-flags/types/feature-flags.types";

export interface NavLink {
  to?: string;
  externalUrl?: string;
  icon: IconSvgElement;
  text: string;
  roles?: UserRole[];
  /** When set, the link only appears if this waffle flag is active. */
  featureFlag?: FeatureFlagName;
  subLinks?: NavLink[];
}

export const omsLinks: NavLink[] = [
  {
    to: "/",
    icon: LayoutDashboard,
    text: "Dashboard",
    roles: ["ADMIN", "STAFF", "PERSONAL", "BUSINESS_MANAGER", "BUSINESS_EMPLOYEE"],
  },
  {
    text: "View Orders",
    to: "/orders/all",
    icon: List,
    roles: ["PERSONAL", "BUSINESS_MANAGER", "BUSINESS_EMPLOYEE"],
  },
  {
    icon: ListChecks,
    text: "Manage Orders",
    to: "/orders/manage",
    roles: ["ADMIN", "STAFF"],
  },
  {
    icon: FolderOpen,
    text: "Projects",
    to: "/projects",
    roles: ["ADMIN", "STAFF"],
  },
  {
    icon: ShoppingCart,
    text: "Orders",
    roles: ["ADMIN", "STAFF", "PERSONAL", "BUSINESS_MANAGER", "BUSINESS_EMPLOYEE"],
    subLinks: [
      {
        text: "Partially Completed",
        to: "/orders/partially-completed",
        icon: Package,
        roles: ["ADMIN", "STAFF"],
      },
      {
        text: "Invoices",
        to: "/invoices",
        icon: DollarSign,
        roles: ["PERSONAL", "BUSINESS_MANAGER", "BUSINESS_EMPLOYEE"],
      },
      {
        text: "Late Orders",
        to: "/orders/late",
        icon: Clock,
        roles: ["ADMIN", "STAFF"],
      },
      {
        text: "Completed",
        to: "/orders/completed",
        icon: Box,
        roles: ["ADMIN", "STAFF"],
      },
      {
        text: "Completed/Shipped Log",
        to: "/orders/completion-log",
        icon: FileCheck,
        roles: ["ADMIN", "STAFF"],
      },
      {
        text: "Completed Cabinets",
        to: "/orders/completed-cabinets",
        icon: Archive,
        roles: ["ADMIN", "STAFF"],
      },
      {
        text: "Scanning",
        to: "/scan",
        icon: QrCode,
        roles: ["ADMIN", "STAFF", "PERSONAL", "BUSINESS_MANAGER", "BUSINESS_EMPLOYEE"],
      },
      {
        text: "Remake Requests",
        to: "/orders/remakes",
        icon: RotateCcw,
        roles: ["ADMIN", "STAFF"],
      },
    ],
  },
  {
    icon: Users,
    text: "Customers",
    roles: ["ADMIN"],
    subLinks: [
      { text: "Accounts", to: "/crm/accounts", icon: Users, roles: ["ADMIN"] },
      { text: "Contacts", to: "/crm/contacts", icon: UserRound, roles: ["ADMIN"] },
      { text: "Contact Roles", to: "/crm/roles", icon: UserCog, roles: ["ADMIN"] },
    ],
  },
  {
    icon: Users,
    text: "Manage Accounts",
    roles: ["ADMIN"],
    subLinks: [
      { text: "Staff", to: "/settings/users/staff", icon: UserCog, roles: ["ADMIN"] },
      { text: "Manage Users", to: "/settings/users/all", icon: UserPlus, roles: ["ADMIN"] },
      { text: "Access Requests", to: "/settings/users/requests", icon: UserSearch, roles: ["ADMIN"] },
    ],
  },
  {
    icon: AlertCircle,
    text: "Issues",
    to: "/issues",
    roles: ["ADMIN", "STAFF"],
    featureFlag: "ai_assistant",
  },
  {
    icon: DollarSign,
    text: "Finance",
    roles: ["ADMIN"],
    subLinks: [
      { text: "Monthly Close", to: "/finance/reports/monthly-close", icon: FileText, roles: ["ADMIN"] },
      { text: "Aging Reports", to: "/finance/reports/aging", icon: FileText, roles: ["ADMIN"] },
      { text: "Bid Volume", to: "/orders/created", icon: BarChart, roles: ["ADMIN", "STAFF"] },
      { text: "Invoices", to: "/invoices", icon: DollarSign, roles: ["ADMIN"] },
      { text: "Payments Received", to: "/payment/receipts", icon: Receipt, roles: ["ADMIN"] },
      { text: "Project Invoices", to: "/billing/project-invoices", icon: Receipt, roles: ["ADMIN"] },
      { text: "Revenue", to: "/finance/reports/revenue", icon: CreditCard, roles: ["ADMIN"] },
      { text: "Payment Settings", to: "/finance/payments", icon: Cog, roles: ["ADMIN"] },
      { text: "Discounts", to: "/finance/discounts", icon: Percent, roles: ["ADMIN"] },
    ],
  },
  {
    icon: ClipboardList,
    text: "Catalog (Internal)",
    roles: ["ADMIN"],
    subLinks: [
      { text: "2020 Upload", to: "/catalog/2020/upload", icon: Upload, roles: ["ADMIN"] },
      { text: "Items", to: "/catalog/items", icon: FileArchive, roles: ["ADMIN"] },
      { text: "Public", to: "/catalog/public", icon: Layers, roles: ["ADMIN"] },
      { text: "Attachments/Add-ons", to: "/catalog/attachments", icon: Paperclip, roles: ["ADMIN"] },
      { text: "Wood Species", to: "/catalog/premiums/wood_species", icon: TreePine, roles: ["ADMIN"] },
      { text: "Door Styles", to: "/catalog/premiums/door_styles", icon: Door, roles: ["ADMIN"] },
      { text: "Finish Options", to: "/catalog/premiums/finish_options", icon: Paintbrush, roles: ["ADMIN"] },
      { text: "Finished Sides", to: "/catalog/premiums/finished_sides", icon: PanelRight, roles: ["ADMIN"] },
      { text: "Construction Methods", to: "/catalog/premiums/construction_methods", icon: Cog, roles: ["ADMIN"] },
    ],
  },
  {
    icon: Users,
    text: "Management",
    roles: ["BUSINESS_MANAGER"],
    subLinks: [
      { text: "View Users", to: "/settings/users/all", icon: List, roles: ["BUSINESS_MANAGER"] },
    ],
  },
  {
    icon: Settings,
    text: "Settings",
    subLinks: [
      {
        text: "My Profile",
        to: "/settings/profile",
        icon: UserRound,
        roles: ["ADMIN", "PERSONAL", "BUSINESS_MANAGER", "BUSINESS_EMPLOYEE"],
      },
      {
        text: "Addresses",
        to: "/settings/address",
        icon: MapPin,
        roles: ["PERSONAL", "BUSINESS_EMPLOYEE", "BUSINESS_MANAGER"],
      },
      { text: "Global Settings", to: "/settings/global", icon: Settings, roles: ["ADMIN"] },
      { text: "AI Prompts", to: "/settings/prompts", icon: AiBrainIcon, roles: ["ADMIN"], featureFlag: "ai_assistant" },
      { text: "Email Announcement", to: "/settings/email/announcement", icon: Megaphone, roles: ["ADMIN"] },
      { text: "Proposal Templates", to: "/settings/proposal-templates", icon: FileText, roles: ["ADMIN"] },
      { text: "External Apps", to: "/settings/api-keys", icon: KeyRound, roles: ["ADMIN"] },
    ],
  },
  {
    icon: BookOpen,
    text: "Catalog",
    externalUrl: "template/catalog/",
  },
  {
    icon: FolderOpen,
    text: "2020 Catalog",
    externalUrl: "template/catalog/2020/",
  },
  // Bank Accounts nav removed — ACH now collects the bank in-session via
  // Stripe Checkout, so the saved-bank add/list flow is obsolete.
];
